import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  CompleteHours,
  Account,
  AccountRole,
  SensitiveProfileField,
  AdminUpdateProfileInput,
  AssignmentCompletion,
  Availability,
  Client,
  ClientStatus,
  CreateWorkflowStepInput,
  Job,
  JobStatus,
  JobType,
  Level,
  UpdateLevelInput,
  NoteEntityType,
  UpdateAssignmentInput,
  DeductAssignmentHoursInput,
  UpdateClientInput,
  UpdateJobInput,
  UpdateJobTypeInput,
  UpdateNoteInput,
  UpdateWorkflowInput,
  UpdateWorkflowStepInput,
  UpdateRuntimeInput,
  DispatchTaskInput,
  UpdateAnnouncementInput,
  PaymentSheet,
  PlayerStatement,
  UpdateSheetTaskInput,
  UpdateSheetPlayerInput,
  ClientBillingStatus,
} from '@workflow/contracts'
import { notify } from '@workflow/ui'
import { api, errorSentence } from './client'
import { keys } from './keys'
import { personName } from '@/lib/people'

// ---- Account ----------------------------------------------------------------

/** Who is signed in, and what sign-in offers. The gate reads this. */
export const useSession = () => useQuery({ queryKey: keys.session, queryFn: api.session })

export const useProfile = () => useQuery({ queryKey: keys.profile, queryFn: api.profile })

export const useMembers = (enabled = true) => useQuery({ queryKey: keys.members, queryFn: api.members, enabled })

export function useMember(id: string | undefined) {
  return useQuery({ queryKey: keys.member(id ?? ''), queryFn: () => api.member(id!), enabled: !!id })
}

export const useActivity = () => useQuery({ queryKey: keys.activity, queryFn: api.activity })
export const useStudioHours = (month: string) =>
  useQuery({ queryKey: keys.studioHours(month), queryFn: () => api.studioHours(month), placeholderData: (previous) => previous })

/** A crew member's name for a toast, the way the crew list names them. */
const assigneeName = (assignment: unknown) => {
  const row = assignment as { playerDiscordName?: string | null; playerName?: string | null } | null
  return row?.playerDiscordName ?? row?.playerName ?? 'They'
}

/** The account's name for a toast — the address when no name was given. */
const accountName = (account: unknown) => {
  const member = account as Account | null
  return member ? personName(member) : 'They'
}

/**
 * Mutations invalidate broadly; there is no live stream yet to do it sooner.
 *
 * Every action announces itself: the success toast in the action's own words
 * when the hook knows them, and — for every hook, no exceptions — the
 * refusal as a toast carrying the server's sentence. A form that shows its
 * error inline still shows it there; the toast is the console-wide guarantee
 * that no failure ever happens in silence.
 */
function useAction<A>(
  action: (arg: A) => Promise<unknown>,
  sentence?: (data: unknown, arg: A) => string,
  afterSuccess?: (data: unknown, arg: A) => void,
) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: action,
    onSuccess: (data, arg) => {
      if (sentence) notify({ tone: 'success', title: sentence(data, arg) })
      afterSuccess?.(data, arg)
    },
    onError: (error) => notify({ tone: 'danger', title: 'That did not go through.', body: errorSentence(error) }),
    onSettled: () => client.invalidateQueries(),
  })
}

/**
 * Sign-in and sign-out reset the cache instead of refreshing it: whatever the
 * previous session was allowed to read, the next one must ask for again.
 * The session probe is asked again first — the gate re-renders the moment its
 * answer changes — and only then is the rest of the cache dropped, when its
 * readers are already unmounted. `clear()` alone cannot do this: it empties
 * the cache but leaves the mounted observers holding their last word, so the
 * gate never hears the sign-out and the page stands still. The gate is the
 * narrator for these — they get no toast.
 */
function useResetAction<A>(action: (arg: A) => Promise<unknown>) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: action,
    onSettled: async () => {
      await client.invalidateQueries({ queryKey: keys.session })
      client.removeQueries({ predicate: (query) => query.queryKey !== keys.session })
    },
  })
}

export const useDevSignIn = () => useResetAction(api.devSignIn)
export const useSignOut = () => useResetAction(api.signOut)
export const useSubmitProfile = () => useAction(api.submitProfile, () => 'The profile is saved.')
export const useAcceptTeamTerms = () => useAction(api.acceptTeamTerms, () => 'The terms were accepted.')

/**
 * Ending another device's session is an ordinary action. Ending *every*
 * session ends the one under this browser too, so it resets the cache like
 * sign-out does — the gate re-asks who it is and lands the reader at the
 * sign-in screen.
 */
export const useSessions = () => useQuery({ queryKey: keys.sessions, queryFn: api.sessions })
export const useRevokeSession = () => useAction(api.revokeSession, () => 'Session signed out.')
export const useRevokeAllSessions = () => useResetAction(api.revokeAllSessions)

// Account actions also invalidate everything: a session that changes status
// changes what every other query is even allowed to ask for.
export const useWhitelistMember = () =>
  useAction(api.whitelistMember, (_data, input) => `${input.email} added to the allowlist.`)
export const useApproveMember = () => useAction(api.approveMember, (data) => `${accountName(data)} was approved.`)
export const useRejectMember = () =>
  useAction(api.rejectMember, (data) => `${accountName(data)}'s request was declined.`)
export const useSuspendMember = () =>
  useAction(api.suspendMember, (data) => `${accountName(data)} was suspended.`)
export const useRevokeMember = () => useAction(api.revokeMember, (data) => `${accountName(data)} was revoked.`)
export const useRestoreMember = () => useAction(api.restoreMember, (data) => `${accountName(data)} was restored.`)
export const useDeleteMember = () => useAction(api.deleteMember, () => 'The account is gone.')

const ROLE_SENTENCE: Record<AccountRole, string> = {
  staff: 'staff',
  player: 'a player',
  admin: 'an administrator',
}

export function useChangeMemberRole() {
  return useAction(
    ({ id, role }: { id: string; role: AccountRole }) => api.changeMemberRole(id, role),
    (data, { role }) => `${accountName(data)} is now ${ROLE_SENTENCE[role]}.`,
  )
}

export function useSetMemberLevel() {
  return useAction(
    ({ id, levelId }: { id: string; levelId: string; name: string }) => api.setMemberLevel(id, levelId),
    (data, { name }) => `${accountName(data)} is now on ${name}.`,
  )
}

/**
 * A reveal is no refresh of anything: the answer is held by the one reader
 * who asked, and the trail has already recorded the asking. Only failures
 * speak — the value itself is the success message.
 */
export function useRevealMemberFields() {
  return useMutation({
    mutationFn: ({ id, fields }: { id: string; fields: SensitiveProfileField[] }) => api.revealMemberFields(id, fields),
    onError: (error) =>
      notify({ tone: 'danger', title: 'Could not reveal the value.', body: errorSentence(error) }),
  })
}

export const useUpdateMemberProfile = () =>
  useAction(
    ({ id, input }: { id: string; input: AdminUpdateProfileInput }) => api.updateMemberProfile(id, input),
    (data) => `${accountName(data)}'s record is saved.`,
  )

// ---- Clients ----------------------------------------------------------------

export const useClients = () => useQuery({ queryKey: keys.clients, queryFn: api.clients })

export function useClient(id: string | undefined) {
  return useQuery({ queryKey: keys.client(id ?? ''), queryFn: () => api.client(id!), enabled: !!id })
}

export const useCreateClient = () =>
  useAction(api.createClient, (data) => `Client created: ${(data as Client).name}`)
export const useUpdateClient = () =>
  useAction(({ id, input }: { id: string; input: UpdateClientInput }) => api.updateClient(id, input), () => 'The client record is saved.')

export function useSetClientStatus() {
  return useAction(
    ({ id, status }: { id: string; status: Extract<ClientStatus, 'active' | 'inactive'> }) =>
      api.setClientStatus(id, status),
    (data, { status }) => `${(data as Client).name} is ${status === 'active' ? 'active' : 'resting'}.`,
  )
}

export const useArchiveClient = () =>
  useAction(api.archiveClient, (data) => `${(data as Client).name} is archived.`)
export const useRestoreClient = () =>
  useAction(api.restoreClient, (data) => `${(data as Client).name} was restored.`)
export function useDeleteClient(onDeleted: () => void) {
  return useAction(
    ({ id }: { id: string; name: string }) => api.deleteClient(id),
    (_data, { name }) => `${name} is deleted. Their jobs and payments keep the name.`,
    onDeleted,
  )
}

// ---- Jobs ---------------------------------------------------------------------

export const useJobTypes = () => useQuery({ queryKey: keys.jobTypes, queryFn: api.jobTypes })

/** Every kind, living and retired — the Settings pane's own reading. */
export const useAllJobTypes = () => useQuery({ queryKey: keys.jobTypesAll, queryFn: api.allJobTypes })

export const useJobs = () => useQuery({ queryKey: keys.jobs, queryFn: api.jobs })
export const usePaymentSheets = () => useQuery({ queryKey: keys.paymentSheets, queryFn: api.paymentSheets })
export const useArchivedPaymentSheets = () => useQuery({ queryKey: [...keys.paymentSheets, 'archived'], queryFn: api.archivedPaymentSheets })
export const usePaymentSheet = (id: string) => useQuery({ queryKey: keys.paymentSheet(id), queryFn: () => api.paymentSheet(id) })
export const useCreatePaymentSheet = (onCreated: (id: string) => void) => useAction(api.createPaymentSheet,
  (data) => `${(data as PaymentSheet).title} sheet created.`, (data) => onCreated((data as PaymentSheet).id))
export const useRefreshPaymentSheet = () => useAction(api.refreshPaymentSheet, () => 'The sheet is up to date with the jobs.')
export const useSharePaymentSheet = () => useAction(api.sharePaymentSheet, () => 'Shared with the players on the sheet.')
export const useArchivePaymentSheet = (onDone: () => void) => useAction(api.archivePaymentSheet, () => 'Sheet archived.', onDone)
export const useRestorePaymentSheet = () => useAction(api.restorePaymentSheet, (data) => `${(data as PaymentSheet).title} sheet restored.`)
export const useSetSheetRate = () => useAction(({ id, jobId, input }: { id: string; jobId: string; input: UpdateSheetTaskInput }) => api.setSheetRate(id, jobId, input))
export const useSetSheetBilling = () => useAction(({ id, clientKey, status }: { id: string; clientKey: string; status: ClientBillingStatus }) => api.setSheetBilling(id, clientKey, status))
export const useUpdateSheetPlayer = () => useAction(({ id, playerId, input }: { id: string; playerId: string; input: UpdateSheetPlayerInput }) => api.updateSheetPlayer(id, playerId, input))
export const useResendStatement = () => useAction(({ id, playerId }: { id: string; playerId: string }) => api.resendStatement(id, playerId), () => 'Sent back to the player to review.')
export const useMyStatements = () => useQuery({ queryKey: keys.statements, queryFn: api.myStatements })
export function useOpenStatement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: api.openStatement,
    onSuccess: ({ openedAt }, sheetId) => {
      queryClient.setQueryData<PlayerStatement[]>(keys.statements, (list) =>
        list?.map((entry) => entry.sheetId === sheetId ? { ...entry, openedAt } : entry))
      void queryClient.invalidateQueries({ queryKey: keys.statements })
    },
    onError: (error) => notify({ tone: 'danger', title: 'Could not mark the statement as opened.', body: errorSentence(error) }),
  })
}

export const useAcceptStatement = () => useAction(api.acceptStatement, () => 'Statement accepted.')
export const useDisputeStatement = (onDone: () => void) => useAction(({ sheetId, reason }: { sheetId: string; reason: string }) => api.disputeStatement(sheetId, reason),
  () => 'Dispute sent to the studio.', onDone)

export function useJob(id: string | undefined) {
  return useQuery({ queryKey: keys.job(id ?? ''), queryFn: () => api.job(id!), enabled: !!id })
}

export const useCreateJob = () => useAction(api.createJob, (data) => `Job created: ${(data as Job).title}`)
export const useUpdateJob = () =>
  useAction(({ id, input }: { id: string; input: UpdateJobInput }) => api.updateJob(id, input), () => 'The job is saved.')

const JOB_STATUS_SENTENCE: Record<JobStatus, string> = {
  draft: 'The job is a draft.',
  published: 'The job is published.',
  in_progress: 'Work has started.',
  on_hold: 'The job is paused.',
  completed: 'The job is complete.',
  removed: 'The job is marked removed. The hours worked still count.',
  cancelled: 'The job was cancelled.',
}

export function useSetJobStatus() {
  return useAction(
    ({ id, status }: { id: string; status: JobStatus }) => api.setJobStatus(id, status),
    (_data, { status }) => JOB_STATUS_SENTENCE[status],
  )
}

const scheduleDay = new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
export const useScheduleJob = () =>
  useAction(
    ({ id, at }: { id: string; at: string }) => api.scheduleJob(id, at),
    (_data, { at }) => `Scheduled to publish ${scheduleDay.format(new Date(at))}.`,
  )
export const useUnscheduleJob = () => useAction(({ id }: { id: string }) => api.unscheduleJob(id), () => 'The schedule is cancelled; the job stays a draft.')

export const useDuplicateJob = (onDuplicated: (job: Job) => void) =>
  useAction(({ id }: { id: string }) => api.duplicateJob(id),
    (data) => `Draft created: ${(data as Job).title}`,
    (data) => onDuplicated(data as Job))

export const useArchiveJob = () =>
  useAction(({ id }: { id: string }) => api.archiveJob(id), () => 'The job is archived.')

export const useRestoreJob = () =>
  useAction(({ id }: { id: string }) => api.restoreJob(id), () => 'The job is restored.')

export function useDeleteJob(onDeleted: () => void) {
  return useAction(
    ({ id }: { id: string }) => api.deleteJob(id),
    (_data, { title }: { id: string; title?: string }) => `The job${title ? ` “${title}”` : ''} is gone.`,
    onDeleted,
  )
}

// ---- The kinds of work ----------------------------------------------------------

export const useLevels = (enabled = true) => useQuery({ queryKey: keys.levels, queryFn: api.levels, enabled })

export const useCreateLevel = () =>
  useAction(api.createLevel, (data) => `Level added: ${(data as Level).name}`)

export const useUpdateLevel = () =>
  useAction(
    ({ id, input }: { id: string; input: UpdateLevelInput }) => api.updateLevel(id, input),
    (data) => `“${(data as Level).name}” is saved.`,
  )

export const useCreateJobType = () =>
  useAction(api.createJobType, (data) => `Category created: ${(data as JobType).name}`)

export const useUpdateJobType = () =>
  useAction(
    ({ id, input }: { id: string; input: UpdateJobTypeInput }) => api.updateJobType(id, input),
    (data) => `“${(data as JobType).name}” is saved.`,
  )

export function useRetireJobType() {
  return useAction(
    ({ id }: { id: string }) => api.retireJobType(id),
    (data) => `“${(data as JobType).name}” is retired — no new work will be named by it.`,
  )
}

export function useRestoreJobType() {
  return useAction(
    ({ id }: { id: string }) => api.restoreJobType(id),
    (data) => `“${(data as JobType).name}” is offered again.`,
  )
}

// ---- The workflows ---------------------------------------------------------------

/** A client's templates. One list per client; the acts invalidate broadly. */
export function useWorkflows(clientId: string | undefined) {
  return useQuery({ queryKey: keys.workflows(clientId ?? ''), queryFn: () => api.workflows(clientId!), enabled: !!clientId })
}

export const useCreateWorkflow = () =>
  useAction(api.createWorkflow, (data) => `Workflow created: ${(data as { name: string }).name}`)

export const useUpdateWorkflow = () =>
  useAction(
    ({ id, input }: { id: string; input: UpdateWorkflowInput }) => api.updateWorkflow(id, input),
    () => 'The workflow is saved.',
  )

export const useDeleteWorkflow = () =>
  useAction(({ id }: { id: string }) => api.deleteWorkflow(id), () => 'The workflow is gone.')

export const useAddWorkflowStep = () =>
  useAction(
    ({ workflowId, input }: { workflowId: string; input: CreateWorkflowStepInput }) =>
      api.addWorkflowStep(workflowId, input),
    () => 'The step joins the workflow.',
  )

export const useUpdateWorkflowStep = () =>
  useAction(
    ({ workflowId, stepId, input }: { workflowId: string; stepId: string; input: UpdateWorkflowStepInput }) =>
      api.updateWorkflowStep(workflowId, stepId, input),
    () => 'The step is saved.',
  )

export const useRemoveWorkflowStep = () =>
  useAction(
    ({ workflowId, stepId }: { workflowId: string; stepId: string }) => api.removeWorkflowStep(workflowId, stepId),
    () => 'The step is gone.',
  )

export const useReorderWorkflowSteps = () =>
  useAction(
    ({ workflowId, ids }: { workflowId: string; ids: string[] }) => api.reorderWorkflowSteps(workflowId, ids),
    () => 'The steps are in order.',
  )

export function useInstantiateWorkflow() {
  return useAction(
    api.instantiateWorkflow,
    (data) => {
      const made = data as Job[]
      return made.length === 1
        ? `The work is open: ${made[0]!.title}`
        : `The work is open: ${made.length} jobs from the workflow.`
    },
  )
}

// ---- Assignments ---------------------------------------------------------------

export function useAssignments(jobId: string | undefined) {
  return useQuery({ queryKey: keys.assignments(jobId ?? ''), queryFn: () => api.assignments(jobId!), enabled: !!jobId })
}

export const useAssignmentPlayers = (enabled: boolean) =>
  useQuery({ queryKey: ['assignment-players'], queryFn: api.assignmentPlayers, enabled })

export const usePlayerGroups = (enabled: boolean) =>
  useQuery({ queryKey: ['player-groups'], queryFn: api.playerGroups, enabled })

export const useSavePlayerGroup = () => useAction(
  ({ id, input }: { id?: string; input: Parameters<typeof api.savePlayerGroup>[0] }) => api.savePlayerGroup(input, id),
  () => 'Player group saved.',
)

export const useDeletePlayerGroup = () => useAction(api.deletePlayerGroup, () => 'Player group deleted.')

export const useAssignPlayers = () => useAction(
  ({ jobId, input }: { jobId: string; input: Parameters<typeof api.assignPlayers>[1] }) => api.assignPlayers(jobId, input),
  () => 'Players assigned.',
)

export const useAssignPlayer = () =>
  useAction(
    ({ jobId, input }: { jobId: string; input: Parameters<typeof api.assignPlayer>[1] }) => api.assignPlayer(jobId, input),
    (data) => `${assigneeName(data)} is on the job.`,
  )

export function useUpdateAssignment() {
  return useAction(
    ({ jobId, assignmentId, input }: { jobId: string; assignmentId: string; input: UpdateAssignmentInput }) =>
      api.updateAssignment(jobId, assignmentId, input),
    () => 'The assignment is saved.',
  )
}

export const useDeductAssignmentHours = () => useAction(
  ({ jobId, assignmentId, input }: { jobId: string; assignmentId: string; input: DeductAssignmentHoursInput }) =>
    api.deductAssignmentHours(jobId, assignmentId, input),
  () => 'The logged hours were adjusted and the player was notified.',
)
export const useAssignmentHourAdjustments = (jobId: string, assignmentId: string) =>
  useQuery({ queryKey: ['hour-adjustments', assignmentId], queryFn: () => api.assignmentHourAdjustments(jobId, assignmentId) })
export const useMyHourAdjustments = (assignmentId: string) =>
  useQuery({ queryKey: ['my-hour-adjustments', assignmentId], queryFn: () => api.myHourAdjustments(assignmentId) })

const COMPLETION_SENTENCE: Record<AssignmentCompletion, (name: string) => string> = {
  assigned: (name) => `${name} is on deck.`,
  in_progress: (name) => `${name} is at work.`,
  completed: (name) => `${name} finished their part.`,
  dropped: (name) => `${name} was dropped from the job.`,
  removed: (name) => `${name} was removed from the job.`,
}

export function useSetAssignmentCompletion() {
  return useAction(
    ({ jobId, assignmentId, input }: { jobId: string; assignmentId: string; input: { completion: AssignmentCompletion; droppedReason?: string | null } }) =>
      api.setAssignmentCompletion(jobId, assignmentId, input),
    (data, { input }) =>
      COMPLETION_SENTENCE[input.completion](assigneeName(data)),
  )
}

// ---- The money ledger ------------------------------------------------------------

// Reading is per subject; the acts invalidate broadly, so a recorded or
// voided row refreshes every trail and total on the page.
export function useJobPayments(jobId: string) {
  return useQuery({ queryKey: keys.payments('job', jobId), queryFn: () => api.payments({ jobId }) })
}

export function useClientPayments(clientId: string) {
  return useQuery({ queryKey: keys.payments('client', clientId), queryFn: () => api.payments({ clientId }) })
}

export function useJobTotals(jobId: string) {
  return useQuery({ queryKey: keys.paymentTotals('job', jobId), queryFn: () => api.paymentTotals({ jobId }) })
}

export function useClientTotals(clientId: string) {
  return useQuery({ queryKey: keys.paymentTotals('client', clientId), queryFn: () => api.paymentTotals({ clientId }) })
}

export function useMyWork() {
  return useQuery({ queryKey: keys.myWork, queryFn: api.myWork })
}

export const useLogHours = () =>
  useAction(({ id, hours }: { id: string; hours: number }) => api.logHours(id, hours), () => 'Your hours are saved.')
export const useSetMyHours = () =>
  useAction(
    ({ id, hours, expectedHours }: { id: string; hours: number; expectedHours: number }) => api.setMyHours(id, hours, expectedHours),
    () => 'Your hours are saved.',
  )
export const useCompleteMyAssignment = () =>
  useAction(({ id, hours }: { id: string; hours?: CompleteHours }) => api.completeMyAssignment(id, hours), () => 'The task is marked complete.')

/**
 * The task window's one save: a note, new hours and the finish, in that
 * order — the note lands first so it reads beside the move it explains —
 * and one sentence for whatever was sent.
 */
export interface MyTaskUpdate {
  id: string
  note?: string
  hours?: { hours: number; expectedHours: number }
  /** Finish the task: with the whole allocation logged, or with only the hours on the counter. */
  complete?: CompleteHours
  /** The hours reach the allocation, so the server completes the task with them. */
  finishes?: boolean
}
export const useUpdateMyTask = () =>
  useAction(
    async ({ id, note, hours, complete }: MyTaskUpdate) => {
      if (note) await api.addMyNote(id, note)
      if (hours) await api.setMyHours(id, hours.hours, hours.expectedHours)
      if (complete) await api.completeMyAssignment(id, complete)
    },
    (_data, { note, hours, complete, finishes }) => {
      const what = complete || finishes ? 'The task is marked complete' : hours ? 'Your hours are saved' : 'Your note was sent'
      return note && (complete || hours) ? `${what}, with your note.` : `${what}.`
    },
  )

const AVAILABILITY_SENTENCE: Record<Availability, string> = {
  active: 'You are clocked in.',
  inactive: 'You are clocked out.',
}
export const useSetAvailability = () =>
  useAction(
    ({ availability }: { availability: Availability }) => api.setAvailability(availability),
    (_data, { availability }) => AVAILABILITY_SENTENCE[availability],
  )

export function usePortalNotes() {
  return useQuery({ queryKey: keys.portalNotes, queryFn: api.portalNotes })
}

export function useMemberWork(id: string | undefined) {
  return useQuery({ queryKey: keys.memberWork(id ?? ''), queryFn: () => api.memberWork(id!), enabled: !!id })
}

export function usePlayerPayments(playerId: string | undefined) {
  return useQuery({
    queryKey: keys.payments('player', playerId ?? ''),
    queryFn: () => api.payments({ playerId: playerId! }),
    enabled: !!playerId,
  })
}

export const useRecordPayment = () => useAction(api.recordPayment, () => 'Payment saved.')

export function useVoidPayment() {
  return useAction(({ id, reason }: { id: string; reason: string }) => api.voidPayment(id, reason), () => 'The payment is voided.')
}

// ---- Notes ---------------------------------------------------------------------

export function useNotes(entityType: NoteEntityType, entityId: string | undefined) {
  return useQuery({
    queryKey: keys.notes(entityType, entityId ?? ''),
    queryFn: () => api.notes(entityType, entityId!),
    enabled: !!entityId,
  })
}

export const useAddNote = () => useAction(api.addNote, () => 'Note added.')

export function useUpdateNote() {
  return useAction(({ id, input }: { id: string; input: UpdateNoteInput }) => api.updateNote(id, input), () => 'The note is saved.')
}

// ---- The bell ------------------------------------------------------------------

export function useNotifications() {
  return useQuery({ queryKey: keys.notifications, queryFn: api.notifications })
}

/** Marking heard is quiet work — no toast for the success, only for a refusal. */
export function useMarkNotificationRead() {
  return useAction(({ id }: { id: string }) => api.markNotificationRead(id))
}

export const useMarkAllNotificationsRead = () =>
  useAction(() => api.markAllNotificationsRead(), () => 'All caught up.')

export const useAnnouncements = () => useQuery({ queryKey: keys.announcements, queryFn: api.announcements })
export const useAllAnnouncements = (enabled = true) =>
  useQuery({ queryKey: keys.allAnnouncements, queryFn: api.allAnnouncements, enabled })
export const useMarkAnnouncementSeen = () => useAction(({ id }: { id: string }) => api.markAnnouncementSeen(id))
export const useCreateAnnouncement = (onDone?: () => void) =>
  useAction(api.createAnnouncement, () => 'The announcement is published.', () => onDone?.())
export const useUpdateAnnouncement = (onDone?: () => void) =>
  useAction(({ id, input }: { id: string; input: UpdateAnnouncementInput }) => api.updateAnnouncement(id, input), () => 'The announcement is updated.', () => onDone?.())
export const useVote = () =>
  useAction(({ id, optionId }: { id: string; optionId: string }) => api.voteAnnouncement(id, optionId))
export const useClosePoll = () => useAction(api.closeAnnouncementPoll, () => 'The poll is closed.')
export const useArchiveAnnouncement = () => useAction(api.archiveAnnouncement, () => 'The announcement is archived.')
export const useRestoreAnnouncement = () => useAction(api.restoreAnnouncement, () => 'The announcement is restored.')
export const usePinAnnouncement = () => useAction(api.pinAnnouncement, () => 'Pinned. It stays at the top for everyone.')
export const useUnpinAnnouncement = () => useAction(api.unpinAnnouncement, () => 'The announcement is unpinned.')

// ---- Service tokens -------------------------------------------------------------

export const useApiTokens = () => useQuery({ queryKey: keys.apiTokens, queryFn: api.apiTokens })

export function useCreateApiToken() {
  return useAction(api.createApiToken, () => 'API token created.')
}

export const useDeleteApiToken = () => useAction(api.deleteApiToken, () => 'API key deleted.')

export function useRevokeApiToken() {
  return useAction(({ id }: { id: string }) => api.revokeApiToken(id), () => 'The key no longer opens anything.')
}

// ---- Runtimes --------------------------------------------------------------------

export const useRuntimes = () => useQuery({ queryKey: keys.runtimes, queryFn: api.runtimes })

export function useCreateRuntime() {
  return useAction(api.createRuntime, () => 'The runtime is enrolled.')
}

export function useUpdateRuntime() {
  return useAction(
    ({ id, input }: { id: string; input: UpdateRuntimeInput }) => api.updateRuntime(id, input),
    () => 'The runtime is saved.',
  )
}

export function useRetireRuntime() {
  return useAction(({ id }: { id: string }) => api.retireRuntime(id), () => 'The runtime is retired.')
}

export const useJobDispatch = (jobId: string) =>
  useQuery({ queryKey: keys.jobDispatch(jobId), queryFn: () => api.jobDispatch(jobId) })

export function useDispatchJobTask() {
  return useAction(
    ({ jobId, input }: { jobId: string; input: DispatchTaskInput }) => api.dispatchJobTask(jobId, input),
    () => 'The task is on the queue.',
  )
}

export function useCancelJobTask() {
  return useAction(
    ({ jobId, taskId }: { jobId: string; taskId: string }) => api.cancelJobTask(jobId, taskId),
    () => 'The task is called back.',
  )
}
